import { Button, Icon, Skeleton, SxProps } from "@outschool/backpack";
import { faBars, faChartLine, faSignIn, faUsersCog } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  joinMembershipPath,
  onboardingQuizPath,
  QuizVariant,
  sellerOrgPath,
} from "@outschool/routes";
import { useEsaExperience, useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useUserWalletSummary } from "@outschool/ui-components-website";
import { useNavigation } from "@outschool/ui-utils";
import omit from "lodash/omit";
import React from "react";
import { useLocation } from "react-router";

import { FeatureFlag } from "../../../shared/Env";
import * as Routes from "../../../shared/Routes";
import { usePricingOfferAdmin } from "../../hooks/CurrentUser";
import { isBuyerOrgAdmin } from "../../lib/BuyerOrgAdmins";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import { UserMode, useUserMode } from "../../lib/UserMode";
import Link from "../Link";
import { clearOnboardingState } from "../onboarding/sessionStorageUtils";
import { ConciergeNavItem } from "./ConciergeNavItem";
import { DesktopNavTabs } from "./DesktopNavTabs";
import { HeaderMenu, HeaderMenuButtonProps } from "./HeaderMenu";
import { WalletNavItem } from "./WalletNavItem";

export const DesktopHeaderNavigation = React.memo(() => {
  const navItems = useNavItemsForUser();

  return (
    <>
      {navItems.map((NavItem, index) => (
        <NavItem key={index} />
      ))}
    </>
  );
});

const useNavItemsForUser = (): React.FunctionComponent<{}>[] => {
  const { isLoggedIn, currentUser, currentUserHasLoaded } = useSession();
  const userMode = useUserMode();
  const {
    totalBalanceCents: walletBalance,
    loading: walletBalanceLoading,
    pricingOffers,
  } = useUserWalletSummary();
  const hasActiveSubscription = currentUser?.hasSubscription;

  const showWalletFunds =
    (walletBalance > 0 || pricingOffers.length > 0) &&
    !walletBalanceLoading &&
    !hasActiveSubscription;

  const isOrgApplicant = !!currentUser?.latestSellerOrgApplication;

  const isSellerOrg = !!currentUser?.sellerOrg?.currentUserIsOwner;

  const buyerOrgAdminModeEnabled = useFeatureFlag(
    FeatureFlag.BuyerOrgAdminMode,
  );

  const pricingOfferOrgId = usePricingOfferAdmin();

  const isBuyerOrgAdminMode =
    buyerOrgAdminModeEnabled && isBuyerOrgAdmin(currentUser);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const utm_medium = searchParams.get("utm_medium");
  const isAdTraffic =
    utm_medium?.startsWith("ads") || utm_medium?.startsWith("paid");
  const isPaidLandingPage =
    location.pathname.includes("/landing/") ||
    (location.pathname.includes("/online-classes/") && isAdTraffic);
  if (isPaidLandingPage) {
    return [LoginButton, JoinButton];
  }

  if (!currentUserHasLoaded) {
    return [
      () => (
        <Button disabled={true} variant="link">
          <Skeleton
            variant="text"
            // need to scale up because of the scale down transform.
            // this comes out to 424x58 px
            width={424}
            height={58}
          />
        </Button>
      ),
      () => (
        <Button disabled={true} variant="link">
          <Icon
            icon={faBars}
            sx={theme => ({
              fontSize: "2.25rem",
              color: theme.palette.common.black,
            })}
          />
        </Button>
      ),
    ];
  }

  if (!isLoggedIn) {
    return [PricingButton, ConciergeNavItem, LoginButton, JoinButton];
  }

  if (isBuyerOrgAdminMode) {
    return [UserHeaderMenu];
  }

  if (userMode === UserMode.Educator) {
    return [
      ...(isSellerOrg ? [SellerOrgManageButton] : []),
      SwitchToParentUserMode,
      ...(pricingOfferOrgId ? [WalletDashboardButton] : []),
      UserHeaderMenu,
    ];
  }

  return [
    DesktopNavTabs,
    ...(isOrgApplicant ? [SellerOrgManageButton] : []),
    ...(pricingOfferOrgId ? [WalletDashboardButton] : []),
    ...(showWalletFunds ? [() => WalletNavItem({ sx: { my: 10 } })] : []),
    UserHeaderMenu,
  ];
};

const NAV_BUTTON_SX: SxProps = {
  fontWeight: "fontWeightBold",
  color: "grey.900",
};

const PricingButton = () => {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const { isEsaExperience } = useEsaExperience();

  if (isEsaExperience) {
    return null;
  }

  return (
    <TrackedButton
      variant="link"
      component={Link}
      to={joinMembershipPath()}
      trackingName="nav_pricing_link"
      sx={NAV_BUTTON_SX}
    >
      {t("Pricing Plans")}
    </TrackedButton>
  );
};

const SellerOrgManageButton = () => {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  return (
    <TrackedButton
      variant="link"
      component={Link}
      to={sellerOrgPath()}
      trackingName="nav_manage_link"
      startIcon={<Icon icon={faUsersCog} />}
      sx={NAV_BUTTON_SX}
      aria-label={t("manage organization")}
    >
      {t("Manage")}
    </TrackedButton>
  );
};

const LoginButton = () => {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  return (
    <Button
      variant="link"
      component={Link}
      to={Routes.loginPath()}
      trackingName="nav_login_link"
      startIcon={<Icon icon={faSignIn} />}
      sx={NAV_BUTTON_SX}
      aria-label={t("log in")}
    >
      {t("Log In")}
    </Button>
  );
};

const WalletDashboardButton = () => {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  // usePricingOfferAdmin is also used in parent, but will pull from cache so there's no additional query
  const pricingOfferOrgId = usePricingOfferAdmin();
  if (!pricingOfferOrgId) {
    return null;
  }

  return (
    <TrackedButton
      variant="link"
      component={Link}
      to={Routes.pricingOfferAdminDashboardForOrganizationPath(
        pricingOfferOrgId,
      )}
      trackingName="nav_wallet_dashboard_link"
      startIcon={<Icon icon={faChartLine} />}
      sx={NAV_BUTTON_SX}
      aria-label={t("pricing offer admin dashboard")}
    >
      {t("Dashboard")}
    </TrackedButton>
  );
};

const UserHeaderMenu = React.memo(() => {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const [numCloseCount, setNumCloseCount] = React.useState(0);

  return (
    <HeaderMenu
      onClose={() => {
        setNumCloseCount(prev => prev + 1);
      }}
    >
      {({ buttonRef, ...props }: HeaderMenuButtonProps) => (
        <TrackedButton
          variant="link"
          trackingName="desktop_header_menu_open_button"
          trackingUniqueId={`desktop_header_menu_open_button-${numCloseCount}`}
          trackingSharedProperties={{
            numCloseCount,
          }}
          aria-label={t("Menu")}
          ref={buttonRef}
          //eslint-disable-next-line i18next/no-literal-string
          {...omit(props, "isOpen")}
        >
          <Icon
            icon={faBars}
            sx={theme => ({
              fontSize: "2.25rem",
              color: theme.palette.common.black,
            })}
          />
        </TrackedButton>
      )}
    </HeaderMenu>
  );
});

const SwitchToParentUserMode = () => {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  return (
    <TrackedButton
      variant="link"
      component={Link}
      to={Routes.parentRootPath()}
      trackingName="nav_parent_link"
      sx={NAV_BUTTON_SX}
      aria-label={t("switch to parent")}
    >
      {t`Switch to Parent`}
    </TrackedButton>
  );
};

const JoinButton = () => {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const { isEsaExperience } = useEsaExperience();
  const navigate = useNavigation();

  if (isEsaExperience) {
    return null;
  }

  return (
    <TrackedButton
      variant="contained"
      trackingName="nav_try_for_free_button"
      sx={{ height: 44, alignSelf: "center" }}
      onClick={() => {
        clearOnboardingState(); // Clear onboarding state before navigating to quiz
        navigate(onboardingQuizPath(undefined, QuizVariant.TryForFree), {
          hardNav: true,
        });
      }}
    >
      {t`Try for Free`}
    </TrackedButton>
  );
};
